import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';
import { useTranslation } from 'react-i18next';

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'IS TELECOM',
  url: 'https://is-telecom.co',
  logo: 'https://static.readdy.ai/image/91fcefbb4717a40f72927278ebfb44c9/b21d4969bbd2d15083f7f8fa7d846874.png',
  description: 'Leader en énergies renouvelables depuis 2006. Micro-réseaux, pompage solaire, stockage, télécommunication.',
  address: { '@type': 'PostalAddress', addressLocality: 'Cotonou, Bénin', addressCountry: 'BJ' },
  contactPoint: [
    { '@type': 'ContactPoint', telephone: '+229-01-95-89-27-55', email: 'contact@is-telecom.co', contactType: 'customer service', availableLanguage: ['French', 'English'] },
    { '@type': 'ContactPoint', telephone: '+33-7-58-32-93-13', contactType: 'customer service', availableLanguage: ['French', 'English'], description: 'WhatsApp' },
  ],
  openingHoursSpecification: [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '18:00' }],
};

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: 'Information', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const subjectOptions = [
    { value: 'Information', label: t('contact.subjectOptions.info') },
    { value: 'Information technique', label: t('contact.subjectOptions.tech') },
    { value: 'Partenariat', label: t('contact.subjectOptions.partnership') },
    { value: 'Carrières', label: t('contact.subjectOptions.careers') },
    { value: 'Autre', label: t('contact.subjectOptions.other') },
  ];

  const contactFAQSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: t('contact.contactFaqs.0.q'), acceptedAnswer: { '@type': 'Answer', text: t('contact.contactFaqs.0.a') } },
      { '@type': 'Question', name: t('contact.contactFaqs.1.q'), acceptedAnswer: { '@type': 'Answer', text: t('contact.contactFaqs.1.a') } },
      { '@type': 'Question', name: t('contact.contactFaqs.2.q'), acceptedAnswer: { '@type': 'Answer', text: t('contact.contactFaqs.2.a') } },
      { '@type': 'Question', name: t('contact.contactFaqs.3.q'), acceptedAnswer: { '@type': 'Answer', text: t('contact.contactFaqs.3.a') } },
      { '@type': 'Question', name: t('contact.contactFaqs.4.q'), acceptedAnswer: { '@type': 'Answer', text: t('contact.contactFaqs.4.a') } },
    ],
  };

  const contactFaqs = [
    { q: t('contact.contactFaqs.0.q'), a: t('contact.contactFaqs.0.a') },
    { q: t('contact.contactFaqs.1.q'), a: t('contact.contactFaqs.1.a') },
    { q: t('contact.contactFaqs.2.q'), a: t('contact.contactFaqs.2.a') },
    { q: t('contact.contactFaqs.3.q'), a: t('contact.contactFaqs.3.a') },
    { q: t('contact.contactFaqs.4.q'), a: t('contact.contactFaqs.4.a') },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    const WEB3FORMS_KEY = '866fa093-9f9d-4633-8b1e-739fc4d2c3f1';
    if (!WEB3FORMS_KEY) {
      setFormError('Configuration du formulaire incomplète. Veuillez contacter l\'administrateur.');
      setIsSubmitting(false);
      return;
    }

    const form = e.currentTarget as HTMLFormElement;
    const formDataObj = new FormData(form);
    const hpValue = (formDataObj.get('company_alt') as string || '').trim();
    if (hpValue !== '') { setSubmitted(true); setIsSubmitting(false); setTimeout(() => setSubmitted(false), 3000); return; }

    const payload = {
      access_key: WEB3FORMS_KEY,
      from_name: 'IS TELECOM - Formulaire Contact',
      subject: `Nouveau message de ${formData.name} - ${formData.subject}`,
      name: formData.name,
      email: formData.email,
      phone: formData.phone || 'Non renseigné',
      subject_client: formData.subject,
      message: formData.message,
    };

    fetch('https://api.web3forms.com/submit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(async (response) => {
        const responseText = await response.text();
        let parsed; try { parsed = JSON.parse(responseText); } catch { /* noop */ }
        const serverMsg = parsed?.message || responseText;
        if (!response.ok || !parsed?.success || (serverMsg && serverMsg.toLowerCase().includes('spam'))) {
          setFormError(serverMsg || 'Une erreur est survenue. Veuillez réessayer.');
          setIsSubmitting(false); return;
        }
        setSubmitted(true); setIsSubmitting(false);
        setFormData({ name: '', email: '', phone: '', subject: 'Information', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      })
      .catch(() => { setFormError('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.'); setIsSubmitting(false); });
  };

  return (
    <Layout>
      <JsonLd data={contactSchema} />
      <JsonLd data={contactFAQSchema} />
      <section className="relative py-20 bg-background-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://readdy.ai/api/search-image?query=Abstract%20communication%20network%20pattern%20with%20green%20connection%20nodes%20and%20lines%20on%20dark%20background%2C%20modern%20digital%20network%20design%20for%20contact%20page%2C%20dark%20navy%20corporate%20aesthetic%20with%20subtle%20light%20accents&width=1920&height=700&seq=contact-hero-01&orientation=landscape" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-16">
          <h1 className="text-4xl md:text-6xl font-bold font-heading text-white mb-4">{t('contact.pageTitle')}</h1>
          <p className="text-lg md:text-xl text-background-300 max-w-3xl mx-auto">{t('contact.pageSubtitle')}</p>
        </div>
      </section>

      <ScrollReveal variant="fade-up">
      <section className="py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-phone-line', title: t('contact.infoPhone'), lines: ['+229 01 95 89 27 55', 'WhatsApp: +33 7 58 32 93 13'] },
              { icon: 'ri-mail-line', title: t('contact.infoEmail'), lines: ['contact@is-telecom.co'] },
              { icon: 'ri-map-pin-line', title: t('contact.infoAddress'), lines: ['Cotonou, Bénin'] },
              { icon: 'ri-time-line', title: t('contact.infoHours'), lines: [t('contact.hoursLine1'), t('contact.hoursLine2')] },
            ].map((info) => (
              <div key={info.title} className="bg-white rounded-3xl p-6 border border-background-200/70 hover:shadow-md transition-shadow text-center">
                <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${info.icon} text-primary-600 text-2xl w-7 h-7 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-bold font-heading text-foreground-950 mb-3">{info.title}</h3>
                {info.lines.map((line, i) => {
                  if (info.icon === 'ri-phone-line' && line.startsWith('WhatsApp')) {
                    return <a key={i} href="https://wa.me/33758329313" target="_blank" rel="noopener noreferrer" className="block text-primary-600 hover:text-primary-700 text-sm cursor-pointer">{line}</a>;
                  }
                  if (info.icon === 'ri-phone-line') {
                    return <a key={i} href="tel:+2290195892755" className="block text-primary-600 hover:text-primary-700 text-sm cursor-pointer">{line}</a>;
                  }
                  if (info.icon === 'ri-mail-line') {
                    return <a key={i} href="mailto:contact@is-telecom.co" className="block text-primary-600 hover:text-primary-700 text-sm cursor-pointer">{line}</a>;
                  }
                  return <p key={i} className="text-foreground-500 text-sm">{line}</p>;
                })}
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      <ScrollReveal variant="fade-up" delay={100}>
      <section className="py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">{t('contact.formTitle')}</h2>
              <p className="text-foreground-500 mb-8">{t('contact.formSubtitle')}</p>
              {submitted && (
                <div className="bg-primary-50 border border-primary-200/70 rounded-2xl p-4 mb-6 flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-primary-600 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <div><div className="font-semibold text-primary-800">{t('contact.formSuccess')}</div><div className="text-sm text-primary-600">{t('contact.formSuccessMsg')}</div></div>
                </div>
              )}
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <div className="text-sm text-red-700">{formError}</div>
                </div>
              )}
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-2">{t('contact.labelName')} <span className="text-red-500">*</span></label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-background-300/60 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm bg-white" placeholder={t('contact.placeholderName')} />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-2">{t('contact.labelEmail')} <span className="text-red-500">*</span></label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-background-300/60 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm bg-white" placeholder={t('contact.placeholderEmail')} />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-2">{t('contact.labelPhone')}</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-background-300/60 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm bg-white" placeholder={t('contact.placeholderPhone')} />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-2">{t('contact.labelSubject')} <span className="text-red-500">*</span></label>
                  <select id="subject" name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 border border-background-300/60 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm bg-white">
                    {subjectOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-2">{t('contact.labelMessage')} <span className="text-red-500">*</span></label>
                  <textarea id="message" name="message" rows={5} value={formData.message} onChange={handleChange} required maxLength={500} className="w-full px-4 py-3 border border-background-300/60 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm bg-white" placeholder={t('contact.placeholderMessage')}></textarea>
                  <div className="text-right text-xs text-foreground-400 mt-1">{formData.message.length}/500</div>
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer disabled:cursor-not-allowed">
                  <span className="flex items-center justify-center gap-2">
                    <i className={`${isSubmitting ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} w-5 h-5 flex items-center justify-center`}></i>
                    {isSubmitting ? t('contact.sending') : t('contact.send')}
                  </span>
                </button>
              </form>
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-3xl overflow-hidden border border-background-200/70 h-80 md:h-96">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12691.0!2d2.4400!3d6.3700!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1023583e8b1d2b4d%3A0x2c8a8f2d6e5b1a3c!2sCotonou%2C%20B%C3%A9nin!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Localisation IS TELECOM" className="grayscale-[20%]"></iframe>
              </div>
              <div className="bg-primary-50 rounded-3xl p-6 border border-primary-100">
                <h3 className="font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-customer-service-2-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  {t('contact.quickResponse')}
                </h3>
                <div className="space-y-3 text-sm text-foreground-600">
                  <p><strong>{t('contact.freeQuote')}</strong> {t('contact.freeQuoteDesc')}</p>
                  <p><strong>{t('contact.techSupport')}</strong> {t('contact.techSupportDesc')}</p>
                  <p><strong>{t('contact.siteVisit')}</strong> {t('contact.siteVisitDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      <ScrollReveal variant="fade-up" delay={100}>
      <section className="py-24 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
              <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{t('faq.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">{t('contact.faqTitle')}</h2>
          </div>
          <div className="space-y-3">
            {contactFaqs.map((faq, i) => (
              <details key={i} className="bg-white rounded-2xl border border-background-200/70 overflow-hidden group">
                <summary className="px-6 py-5 cursor-pointer flex items-center justify-between font-semibold text-foreground-950 text-sm hover:bg-background-50 transition-colors">
                  <span>{faq.q}</span>
                  <i className="ri-add-line text-primary-600 w-5 h-5 flex items-center justify-center group-open:hidden"></i>
                  <i className="ri-subtract-line text-primary-600 w-5 h-5 flex items-center justify-center hidden group-open:flex"></i>
                </summary>
                <div className="px-6 pb-5 text-foreground-500 text-sm leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>
    </Layout>
  );
}